import { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  Tooltip,
  Modal,
  Image,
  message,
  Spin,
} from "antd";
import { ReloadOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  listVehiclePhotos,
  downloadVehiclePhoto,
} from "../../services/vehiclePhotos.service";
import StatusBadge from "../../components/common/StatusBadge";

const { Search } = Input;

function AttachedBadge({ attached }) {
  if (!attached) {
    return (
      <Tag
        style={{
          color: "#6b7280",
          background: "#f3f4f6",
          borderColor: "#e5e7eb",
          fontWeight: 700,
        }}
      >
        NO
      </Tag>
    );
  }
  return (
    <Tag
      style={{
        color: "#15803d",
        background: "#f0fdf4",
        borderColor: "#bbf7d0",
        fontWeight: 700,
      }}
    >
      YES
    </Tag>
  );
}

function blobToObjectUrl(blob) {
  return URL.createObjectURL(blob);
}

/**
 * Next-due state for a rider's vehicle photos. The backend supplies
 * daysUntilDue and isOverdue; this only chooses the wording and colour, so the
 * panel and the rider app cannot disagree about who is overdue.
 */
function NextDueTag({ record }) {
  if (record.isOverdue) {
    return (
      <Tag color="red">
        {record.attachedAt ? "Over Due (Upload Now)" : "Never uploaded — Upload Now"}
      </Tag>
    );
  }
  if (record.daysUntilDue === 0) return <Tag color="orange">Due today</Tag>;
  return (
    <Tag color={record.daysUntilDue <= 3 ? "orange" : "green"}>
      {`Due in ${record.daysUntilDue} day${record.daysUntilDue === 1 ? "" : "s"}`}
    </Tag>
  );
}

export default function VehiclesTab() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total_count: 0,
  });
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState({
    open: false,
    loading: false,
    rider: null,
    urls: [],
  });

  const fetchList = useCallback(
    async (page = pagination.page) => {
      setLoading(true);
      try {
        const res = await listVehiclePhotos({
          page,
          per_page: pagination.per_page,
          search: search || undefined,
        });
        const data = res?.data || {};
        setRows(data.records || []);
        setPagination(
          data.pagination || {
            page,
            per_page: pagination.per_page,
            total_count: 0,
          }
        );
      } catch {
        message.error("Failed to load vehicles list");
      } finally {
        setLoading(false);
      }
    },
    [pagination.page, pagination.per_page, search]
  );

  useEffect(() => {
    fetchList(1);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const closePreview = () => {
    preview.urls.forEach((u) => URL.revokeObjectURL(u));
    setPreview({ open: false, loading: false, rider: null, urls: [] });
  };

  const handleView = async (record) => {
    if (!record.vehiclesAttached) return;
    setPreview({
      open: true,
      loading: true,
      rider: record,
      urls: [],
    });
    try {
      const [b1, b2] = await Promise.all([
        downloadVehiclePhoto(record._id, 1),
        downloadVehiclePhoto(record._id, 2),
      ]);
      const urls = [blobToObjectUrl(b1), blobToObjectUrl(b2)];
      setPreview({
        open: true,
        loading: false,
        rider: record,
        urls,
      });
    } catch {
      message.error("Failed to load vehicle photos");
      setPreview({ open: false, loading: false, rider: null, urls: [] });
    }
  };

  const columns = [
    {
      title: "Rider ID",
      dataIndex: "riderId",
      width: 110,
    },
    {
      title: "Partner Name",
      dataIndex: "fullName",
      width: 180,
    },
    {
      title: "Mobile",
      dataIndex: "mobileNumber",
      width: 130,
    },
    {
      title: "Project",
      dataIndex: "projectName",
      width: 140,
      render: (v) => v || "—",
    },
    {
      title: "Zone",
      dataIndex: "zoneName",
      width: 120,
      render: (v) => v || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      render: (status) => <StatusBadge status={status} />,
    },
    {
      title: "Vehicles Attached",
      dataIndex: "vehiclesAttached",
      width: 150,
      render: (v) => <AttachedBadge attached={v} />,
    },
    {
      title: "Last Upload",
      dataIndex: "attachedAt",
      width: 130,
      render: (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "—"),
    },
    {
      title: "Next Due",
      key: "nextDue",
      width: 190,
      render: (_, record) => <NextDueTag record={record} />,
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      fixed: "right",
      render: (_, record) => (
        <Tooltip
          title={
            record.vehiclesAttached
              ? "View vehicle photos"
              : "No vehicle photos uploaded"
          }
        >
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            disabled={!record.vehiclesAttached}
            onClick={() => handleView(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space wrap style={{ marginBottom: 16 }}>
        <Search
          placeholder="Search rider / mobile"
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={setSearch}
          style={{ width: 280 }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => fetchList(pagination.page)}>
          Refresh
        </Button>
      </Space>

      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1100 }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.per_page,
          total: pagination.total_count,
          showSizeChanger: false,
          onChange: (page) => fetchList(page),
        }}
      />

      <Modal
        title={
          preview.rider
            ? `Vehicle photos — ${preview.rider.fullName} (${preview.rider.riderId})`
            : "Vehicle photos"
        }
        open={preview.open}
        onCancel={closePreview}
        footer={[
          <Button key="close" type="primary" onClick={closePreview}>
            Close
          </Button>,
        ]}
        width={720}
        destroyOnClose
      >
        {preview.loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            {preview.rider ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 16,
                  color: "#6b7280",
                  fontSize: 13,
                }}
              >
                <span>
                  Last Upload:{" "}
                  <strong style={{ color: "#111827" }}>
                    {preview.rider.attachedAt
                      ? dayjs(preview.rider.attachedAt).format("DD-MM-YYYY")
                      : "—"}
                  </strong>
                </span>
                <NextDueTag record={preview.rider} />
              </div>
            ) : null}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {preview.urls.map((url, idx) => (
              <div key={url} style={{ flex: "1 1 280px" }}>
                <div style={{ marginBottom: 8, fontWeight: 600 }}>Photo {idx + 1}</div>
                <Image
                  src={url}
                  alt={`Vehicle photo ${idx + 1}`}
                  style={{ width: "100%", borderRadius: 8 }}
                />
              </div>
              ))}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
